/**
 * Вычистка email из всего, что уходит наружу процесса.
 *
 * Требование — security-checklist.md, п. 3: адрес не попадает ни в один
 * лог и ни в один отчёт об ошибке. Логи и отчёты переживают инцидент,
 * экспортируются и читаются теми, кому персональные данные не нужны.
 *
 * Почему в contracts, а не в приложении: отчёт об ошибке — это
 * сообщение, пересекающее границу процесса (наружу, в сервис
 * мониторинга), и правило о его содержимом одно на сервер и клиент
 * (D17). Дублировать такое правило в двух пакетах — значит однажды
 * поправить его в одном.
 *
 * Фильтрация сделана здесь, а не настройкой сервиса: настройка
 * на стороне сервиса теряется при переезде на другой бэкенд (D17),
 * а код переезжает вместе с приложением.
 */
/** Чем заменяется найденный адрес. Значение видно в отчёте — так
 *  понятно, что поле было и его вычистили, а не что его не прислали. */
export const REDACTED = '[email удалён]';
/** Имена полей, значение которых считается адресом целиком. */
const EMAIL_KEYS = new Set([
    'email',
    'emails',
    'e-mail',
    'mail',
    'useremail',
    'user_email',
    'email_address',
    'emailaddress',
    'from',
    'to',
    'reply_to',
]);
/** Адрес внутри произвольной строки: «пользователь ivan@example.com не найден». */
const EMAIL_IN_TEXT = /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}.-]+\.[\p{L}]{2,}/gu;
/**
 * Глубина, дальше которой не идём. Событие мониторинга — дерево
 * ограниченной глубины; бесконечный обход по нему означает ошибку
 * в данных, а не полезную работу.
 */
const MAX_DEPTH = 12;
function scrubString(value) {
    return value.replace(EMAIL_IN_TEXT, REDACTED);
}
function isPlainRecord(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function scrubValue(value, depth, seen) {
    if (typeof value === 'string')
        return scrubString(value);
    if (value === null || typeof value !== 'object')
        return value;
    if (depth >= MAX_DEPTH)
        return '[слишком глубоко]';
    const asObject = value;
    if (seen.has(asObject))
        return '[циклическая ссылка]';
    seen.add(asObject);
    if (Array.isArray(value)) {
        return value.map((item) => scrubValue(item, depth + 1, seen));
    }
    if (!isPlainRecord(value)) {
        // Ошибки, даты и прочие носители поведения не пересобираем:
        // из них наружу уходит текст, а его чистит вызывающий код
        // (в отчёте мониторинга это делает сам SDK, отдавая plain-объект).
        return value;
    }
    const result = {};
    for (const [key, nested] of Object.entries(value)) {
        if (EMAIL_KEYS.has(key.toLowerCase())) {
            result[key] = REDACTED;
            continue;
        }
        result[key] = scrubValue(nested, depth + 1, seen);
    }
    return result;
}
/**
 * Возвращает копию значения без email: поля с говорящими именами
 * заменены целиком, адреса внутри строк — вырезаны.
 *
 * Функция чистая: исходное значение не меняется.
 */
export function scrubEmails(value) {
    return scrubValue(value, 0, new WeakSet());
}
//# sourceMappingURL=scrub.js.map